import React, { useEffect, useState, Fragment } from "react"
import { useTranslation } from 'react-i18next'

import { formatMoney } from 'helpers/helpers'

// Вкладка «Мотивационные программы» в X- и Z-отчётах: таблица на каждую программу, внутри — сотрудники.
// Строка сотрудника раскрывается в меню (with_items=menu), меню — в продукты, из которых набрался бонус;
// при with_items=1 приходит плоский список позиций без меню. Всё в том же ответе.
function MotivationProgramsTable({ payload }) {
	const { t } = useTranslation()

	const [expandedRow, setExpandedRow] = useState(null)
	const [expandedMenus, setExpandedMenus] = useState({})

	// Новый отчёт — раскрытые строки от старого не переносятся
	useEffect(() => {
		setExpandedRow(null)
		setExpandedMenus({})
	}, [payload])

	const programs = Array.isArray(payload?.data)
		? payload.data
		: (Array.isArray(payload?.motivation_programs) ? payload.motivation_programs : [])

	let totalSales = 0
	let totalBonus = 0
	const groups = programs.map((program) => {
		const users = (Array.isArray(program?.users) ? program.users : []).map((user) => ({
			employee_name: user?.name ?? '',
			sales: Number(user?.sales) || 0,
			bonus: Number(user?.bonus) || 0,
			items: user?.items,
			menus: user?.menus,
			cheques_count: user?.cheques_count,
			per_cheque: user?.per_cheque,
		}))
		const sales = users.reduce((sum, user) => sum + user.sales, 0)
		const bonus = users.reduce((sum, user) => sum + user.bonus, 0)
		totalSales += sales
		totalBonus += bonus
		return { name: program?.name ?? '', users, sales, bonus }
	})

	function formatQuantity(quantity) {
		return formatMoney(quantity, Number.isInteger(Number(quantity)) ? 0 : 3)
	}

	return (
		<div style={{ 'maxHeight': 'calc(100vh - 180px)', 'overflow': 'auto' }}>
			{groups.map((group, gIndex) => (
				<div className="mb-3" key={gIndex}>
					<table className="table table-bordered table-bordered-custom">
						<thead>
							<tr>
								<th style={{ 'width': '40%' }} colSpan={2} className="text-start"><b>{group.name}</b></th>
								<th style={{ 'width': '30%' }} className="text-end text-nowrap"><b>{t('amount')}</b></th>
								<th style={{ 'width': '30%' }} className="text-end text-nowrap"><b>{t('bonus')}</b></th>
							</tr>
						</thead>
						<tbody>
							{group.users.map((item, index) => {
								const rowKey = `${gIndex}-${index}`
								const items = Array.isArray(item.items) ? item.items : []
								const menus = Array.isArray(item.menus) ? item.menus : []
								const isExpanded = expandedRow === rowKey
								const canExpand = items.length > 0 || menus.length > 0
								return (
									<Fragment key={rowKey}>
										<tr className={canExpand ? 'cursor' : ''}
											onClick={() => canExpand && setExpandedRow(isExpanded ? null : rowKey)}>
											<td colSpan={2} className="text-start">
												{canExpand &&
													<i className={'me-1 uil ' + (isExpanded ? 'uil-angle-down' : 'uil-angle-right')}></i>
												}
												{item.employee_name}
											</td>
											<td className="text-end text-nowrap">{formatMoney(item.sales)}</td>
											<td className="text-end text-nowrap">{formatMoney(item.bonus)}</td>
										</tr>
										{/* Бонус за чек: на позиции не делится, показываем число чеков и ставку */}
										{isExpanded && item.cheques_count !== null && item.cheques_count !== undefined &&
											<tr className="text-muted">
												<td colSpan={3} className="text-start ps-4">
													{t('motivation_bonus_per_cheque', { count: item.cheques_count, rate: formatMoney(item.per_cheque) })}
												</td>
												<td className="text-end">{formatMoney(item.bonus)}</td>
											</tr>
										}
										{isExpanded && menus.map((menu, mIndex) => {
											const menuKey = `${rowKey}-${mIndex}`
											const isMenuExpanded = !!expandedMenus[menuKey]
											const menuName = menu.is_service ? t('service_and_delivery') : (menu.name || t('motivation_no_menu'))
											return (
												<Fragment key={menuKey}>
													<tr className="text-muted cursor"
														onClick={() => setExpandedMenus({ ...expandedMenus, [menuKey]: !isMenuExpanded })}>
														<td className="text-start ps-4">
															<i className={'me-1 uil ' + (isMenuExpanded ? 'uil-angle-down' : 'uil-angle-right')}></i>
															{menuName}
														</td>
														{/* Сколько блюд продано в меню; у обслуживания и доставки «количество» не имеет смысла */}
														<td className="text-end">{menu.is_service ? '' : formatQuantity(menu.quantity)}</td>
														<td className="text-end text-nowrap">{formatMoney(menu.amount)}</td>
														<td className="text-end text-nowrap">{menu.bonus !== null && menu.bonus !== undefined ? formatMoney(menu.bonus) : ''}</td>
													</tr>
													{isMenuExpanded && menu.items?.map((product) => (
														<tr key={product.product_id} className="text-muted">
															<td className="text-start ps-5">{product.name}</td>
															<td className="text-end">{formatQuantity(product.quantity)}</td>
															<td className="text-end">{formatMoney(product.amount)}</td>
															<td className="text-end">{product.bonus !== null ? formatMoney(product.bonus) : ''}</td>
														</tr>
													))}
												</Fragment>
											)
										})}
										{isExpanded && items.map((product) => (
											<tr key={product.product_id} className="text-muted">
												<td className="text-start ps-4">{product.name}</td>
												<td className="text-end">{formatQuantity(product.quantity)}</td>
												<td className="text-end">{formatMoney(product.amount)}</td>
												<td className="text-end">{product.bonus !== null ? formatMoney(product.bonus) : ''}</td>
											</tr>
										))}
									</Fragment>
								)
							})}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={2}><b>{t('total')}</b></td>
								<td className="text-end text-nowrap"><b>{formatMoney(group.sales)}</b></td>
								<td className="text-end text-nowrap"><b>{formatMoney(group.bonus)}</b></td>
							</tr>
						</tfoot>
					</table>
				</div>
			))}
			{groups.length > 1 &&
				<table className="table table-bordered table-bordered-custom">
					<tfoot>
						<tr>
							<td style={{ 'width': '40%' }}><b>{t('total')}</b></td>
							<td style={{ 'width': '30%' }} className="text-end text-nowrap"><b>{formatMoney(totalSales)}</b></td>
							<td style={{ 'width': '30%' }} className="text-end text-nowrap"><b>{formatMoney(totalBonus)}</b></td>
						</tr>
					</tfoot>
				</table>
			}
		</div>
	)
}

export default MotivationProgramsTable
